import { useCallback, useState, useEffect, useRef } from 'react';
import _ from 'lodash';
/* eslint-disable restrict-imports/restrict-imports */
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import { matchSorter } from 'match-sorter';
import logger from '@scale/frontend-lib/logging';

import { getFieldMaxResponses } from '@scale/shared/textcollection/defaults';
import {
  AutocompleteChoiceLongHint,
  AutocompleteField,
} from '@scale/shared/types/params/textcollection';
import { AutocompleteFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { makeStyles } from 'design-system/styles';
import { TextCollectionConfig } from '@scale/shared/defaults';
import { ChoiceLongHint } from './CategoryFieldLongHints';
import { fetchLongHints } from '@scale/shared/textcollection/helpers';
import { Chip } from '@material-ui/core';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';

const enabledTurkeyProjects = ['633cd0423dbe8b0018c4b27f'];
let customer: string | undefined;
interface AutocompleteFieldSectionProps {
  field: AutocompleteField;
  annotation: AutocompleteFieldAnnotation;
  setAnnotation: (annotation: AutocompleteFieldAnnotation) => void;
  disabled?: boolean;
  textCollectionConfig?: TextCollectionConfig;
  projectId?: string;
}

export default function AutocompleteFieldSection({
  field,
  annotation,
  setAnnotation,
  disabled,
  textCollectionConfig,
  projectId,
}: AutocompleteFieldSectionProps) {
  const checkEmptyArray = (value: any) => {
    return _.isEmpty(value) || (_.isArray(value) && _.isEmpty(value[0]));
  };

  const [options, setOptions] = useState(field.options);
  const [value, setValue] = useState<string[]>(
    !checkEmptyArray(annotation.response) ? annotation.response : [],
  );
  const [normalizationList, setNormalizationList] = useState([]);
  const [noncanonicalMap, setNoncanonicalMap] = useState({});
  const anchorRef = useRef(null);
  const [fieldLongHints, setFieldLongHints] = useState<
    Record<string, AutocompleteChoiceLongHint[]>
  >({});
  const [hoveredChoiceId, setHoveredChoiceId] = useState<string | null>(null);

  useEffect(() => {
    setFieldLongHints({});
    if (!field.longHintsS3Artifact) {
      return;
    }

    fetchLongHints(field.longHintsS3Artifact, setFieldLongHints);
  }, [field]);

  useEffect(() => {
    customer = getCustomerForNormalization(projectId);
    if (customer) {
      const normalizationDownloadPath = `https://scale-static-assets.s3.amazonaws.com/${customer}/normalized_values_by_attribute_map.json`;
      const mappingDownloadPath = `https://scale-static-assets.s3.amazonaws.com/${customer}/noncanonical_to_normalized_map.json`;

      const getS3Data = async (s3Url: string, setterFunction: any) => {
        const resp = await fetch(s3Url);
        const json = await resp.json();
        const attributeCategory = field.field_id.replace('_autocomplete', '').trim();
        setterFunction(json[attributeCategory]);
      };

      getS3Data(normalizationDownloadPath, setNormalizationList).catch(error => {
        logger.error(`S3 retrieval for ${normalizationDownloadPath} failed: ${error}`);
      });
      getS3Data(mappingDownloadPath, setNoncanonicalMap).catch(error => {
        logger.error(`S3 retrieval for ${mappingDownloadPath} failed: ${error}`);
      });
    }
  }, [field.field_id, projectId]);

  let filterOptions;
  if (noncanonicalMap && normalizationList && textCollectionConfig?.useNormalization) {
    const combinedOptions = Object.entries(noncanonicalMap).concat(
      normalizationList.map((normalizedValue: any) => [normalizedValue, normalizedValue]),
    );

    const optionsObjects: any[] = [];
    combinedOptions.forEach(pair => optionsObjects.push({ label: pair[0], value: pair[1] }));

    filterOptions = (options: string[], { inputValue }: { inputValue: string }): string[] => {
      const output = new Set();
      matchSorter(optionsObjects, inputValue, { keys: ['label'] }).forEach(optionObject =>
        output.add(optionObject.value),
      );
      return Array.from(output).concat([inputValue]) as string[];
    };
  } else {
    filterOptions = (options: string[], { inputValue }: { inputValue: string }) => {
      return matchSorter(options ?? [], inputValue);
    };
  }

  const getCustomerForNormalization = (projectId: string | undefined): string | undefined => {
    let customer: string | undefined = undefined;
    if (enabledTurkeyProjects.includes(projectId ?? '')) {
      customer = 'Turkey';
    }
    return customer;
  };

  const onInputChange = useCallback(
    (event: any, newValue: any) => {
      if (field.allow_custom_options) {
        setOptions([...new Set([...field.options, newValue])]);
      }
    },
    [field, setOptions],
  );

  const onChange = useCallback(
    (event: any, newValue: string | string[] | null) => {
      if (_.isString(newValue)) {
        setValue([newValue]);
        setAnnotation({
          ...annotation,
          response: [newValue],
        });
        return;
      }

      setValue(!checkEmptyArray(newValue) ? (newValue as string[]) : []);
      setAnnotation({
        ...annotation,
        response: newValue ?? [],
      });
    },
    [annotation, setAnnotation, setValue],
  );

  const renderOption = (option: string) => {
    return (
      <div
        className={styles.option}
        onMouseEnter={() => {
          setHoveredChoiceId(option);
        }}
        onMouseLeave={() => {
          setHoveredChoiceId(null);
        }}
        onClick={() => {
          setHoveredChoiceId(null);
        }}
      >
        <div>{option}</div>
      </div>
    );
  };

  const handleDeleteChip = useCallback(
    (event: any, optionToDelete: string) => {
      const updatedValue = value.filter(option => option !== optionToDelete);
      onChange(event, updatedValue);
    },
    [value, onChange],
  );

  const styles = useStyles();
  const maximumFieldResponses = getFieldMaxResponses(field);
  return (
    <div className={styles.container}>
      <FieldHeader title={field.title} description={field.description} hint={field.hint} />
      <div className={styles.rowContainer} ref={anchorRef}>
        <Autocomplete
          multiple={maximumFieldResponses > 1}
          value={value}
          onChange={onChange}
          onInputChange={onInputChange}
          id={`${field.field_id}-autocomplete`}
          filterOptions={filterOptions}
          options={options}
          getOptionLabel={option => {
            if (_.isArray(option)) {
              return option[0];
            }

            return option;
          }}
          renderInput={params => {
            const sortedOptions = _.uniq([...value, ...options]);
            const displayValues = disabled ? sortedOptions : value;
            return (
              <TextField
                {...params}
                variant="outlined"
                className={styles.field}
                placeholder={
                  field.hideMaxResponsesLabel
                    ? 'Select field values'
                    : `Select up to ${maximumFieldResponses} field values`
                }
                label={field.title}
                InputProps={{
                  ...params.InputProps,
                  readOnly: disabled,
                  startAdornment:
                    maximumFieldResponses > 1
                      ? displayValues.map((option: string) => (
                          <Chip
                            key={option}
                            label={option}
                            color={value.includes(option) ? 'secondary' : undefined}
                            style={{ margin: '2px' }}
                            onDelete={
                              !disabled ? event => handleDeleteChip(event, option) : undefined
                            }
                            disabled={disabled}
                          />
                        ))
                      : null,
                }}
              />
            );
          }}
          renderOption={renderOption}
          disabled={disabled}
          className={styles.field}
        />
        {fieldLongHints && hoveredChoiceId && (
          <ChoiceLongHint
            longHints={
              fieldLongHints[hoveredChoiceId] ?? fieldLongHints[hoveredChoiceId?.toLowerCase()]
            }
            isOpen={
              !_.isEmpty(
                fieldLongHints[hoveredChoiceId] ?? fieldLongHints[hoveredChoiceId?.toLowerCase()],
              )
            }
            anchorRef={anchorRef}
          />
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles(theme => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      // alignItems: 'center',
    },
    rowContainer: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
    },
    title: {
      color: '#000000',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      marginTop: '8px',
      marginBottom: '8px',
      display: 'inline-block',
    },
    description: {
      color: theme.colors.gray50,
      marginBottom: 16,
      fontSize: 12,
    },
    field: {
      display: 'flex',
      flex: 1,
      marginRight: '8px',
    },
    option: {
      width: '100%',
    },
    tooltipInfoIcon: {
      paddingLeft: 8,
      paddingRight: 8,
      color: theme.colors.gray50,
      marginTop: 6,
      minWidth: 14,
      minHeight: 14,
    },
    tooltipText: {
      maxWidth: 496,
    },
  };
});
